import React, { createContext, useContext, useState, useEffect } from 'react';
import { authApi, getToken, setToken, removeToken } from '../lib/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is logged in
    const token = getToken();
    if (token) {
      // Decode JWT to get user info (simple decode, not verification)
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        if (payload.exp > Date.now() / 1000) {
          // Token is still valid, fetch user info from localStorage
          const storedUser = localStorage.getItem('user_info');
          if (storedUser) {
            setUser(JSON.parse(storedUser));
          }
        } else {
          // Token expired
          removeToken();
          localStorage.removeItem('user_info');
        }
      } catch (e) {
        removeToken();
        localStorage.removeItem('user_info');
      }
    }
    setLoading(false);
  }, []);

  const login = async (email, password) => {
    const data = await authApi.login(email, password);
    setToken(data.token);
    localStorage.setItem('user_info', JSON.stringify(data.user));
    setUser(data.user);
    return data;
  };

  const register = async (email, password, name, company) => {
    const data = await authApi.register(email, password, name, company);
    setToken(data.token);
    localStorage.setItem('user_info', JSON.stringify(data.user));
    setUser(data.user);
    return data;
  };

  const logout = () => {
    removeToken();
    localStorage.removeItem('user_info');
    setUser(null);
  };

  const value = {
    user,
    loading,
    isAuthenticated: !!user,
    login,
    register,
    logout,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default useAuth;
